'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@repo/design-system/components/ds/button';
import { Logo } from '@repo/design-system/components/ds/logo';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Heading } from '@repo/design-system/components/ui/heading';
import { useHostInfo } from '@repo/host/hooks/use-host-info';
import { css } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectMapName, selectSteamId, selectTitleId, selectTitleName } from '@repo/store/maps/slices/mapData';
import { HostMode, HostType } from '@repo/types/host';
import type { MapInitializationData } from '@repo/types/maps/map-types';
import { Link } from '@repo/ui/components/link/link';
import { getWandSteamCdnImageUrl } from '@repo/utility/image-urls';
import { Suspense } from 'react';
import { useMobileViewportDetection } from '../game-map/hooks/useMobileViewportDetection';
import { useMapDownloadPath } from '../utils/use-map-download-path';
import { LanguageSelector } from './language-selector';
import { MapDetailsButton } from './map-details-button';
import { MapShareButton } from './map-share-button';
import { MapTopBarActions } from './map-top-bar-actions';
import { MapTopBarUserAvatar } from './map-top-bar-user-avatar';
export const MapTopBar = t0 => {
  const $ = _c(61);
  const {
    providedMapData,
    downloadButtonText: t1
  } = t0;
  const downloadButtonText = t1 === undefined ? "Download Wand" : t1;
  const reduxMapName = useAppSelector(selectMapName);
  const reduxTitleName = useAppSelector(selectTitleName);
  const reduxSteamId = useAppSelector(selectSteamId);
  const externalMapId = useAppSelector(selectExternalMapId);
  const titleId = useAppSelector(selectTitleId);
  let t2;
  if ($[0] !== externalMapId || $[1] !== titleId) {
    t2 = {
      mapId: externalMapId,
      titleId
    };
    $[0] = externalMapId;
    $[1] = titleId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const downloadPath = useMapDownloadPath("map_top_bar", t2);
  const gameTitle = reduxTitleName ?? providedMapData.mapConfig.titleName;
  const mapName = reduxMapName ?? providedMapData.mapData.metadata.name;
  const steamId = reduxSteamId ?? providedMapData.mapConfig.steamId;
  const hostInfo = useHostInfo();
  const isMobileViewport = useMobileViewportDetection();
  const isDesktop = hostInfo.hostType === HostType.APP;
  if (isMobileViewport || hostInfo.hostMode === HostMode.EMBED) {
    return null;
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = css({
      backgroundColor: "wemod.elevated.100",
      width: "full",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "[6px 6px 6px 12px]",
      userSelect: "none",
      position: "relative",
      height: "[52px]",
      gap: "12"
    });
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const topBarStyles = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = css({
      display: "flex",
      alignItems: "center",
      height: "[16px]",
      width: "auto",
      flexShrink: 0,
      color: "white"
    });
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const logoStyles = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = css({
      display: "flex",
      alignItems: "center",
      gap: "8",
      justifyContent: "center",
      minWidth: "0",
      minHeight: "[40px]",
      overflow: "hidden",
      flex: "1",
      _compactUp: {
        position: "absolute",
        left: "[50%]",
        transform: "translateX(-50%)",
        flex: "none"
      },
      _compact: {
        justifyContent: "flex-start"
      }
    });
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const gameInfoStyles = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = css({
      height: "[20px]",
      width: "[20px]",
      objectFit: "cover",
      borderRadius: "4",
      flexShrink: 0
    });
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const gameImageStyles = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = css.raw({
      fontSize: "lg",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      flexShrink: "1",
      minWidth: "0"
    });
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const gameTitleStyles = t7;
  let t8;
  if ($[8] !== isDesktop) {
    t8 = css.raw({
      display: "flex",
      alignItems: "center",
      minWidth: "[200px]",
      justifyContent: "flex-end",
      flexShrink: 0,
      marginRight: isDesktop ? "[150px]" : "0"
    });
    $[8] = isDesktop;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const actionsContainerStyles = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      WebkitAppRegion: "drag"
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = css.raw({
      flexShrink: 0
    });
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = css({
      flexShrink: 0
    });
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Logo variant="wordmark" aria-label="Wand logo" height="[16px]" />;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== logoStyles) {
    t13 = <span className={logoStyles}>{t12}</span>;
    $[14] = logoStyles;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t11 || $[17] !== t13) {
    t14 = <Link href="/" className={t11}>{t13}</Link>;
    $[16] = t11;
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== t10 || $[20] !== t14) {
    t15 = <FlexRow align="center" gap="12" className={t10}>{t14}</FlexRow>;
    $[19] = t10;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== gameImageStyles || $[23] !== gameTitle || $[24] !== gameTitleStyles || $[25] !== mapName || $[26] !== steamId) {
    t16 = mapName && gameTitle && <>{steamId && <img className={gameImageStyles} src={getWandSteamCdnImageUrl(steamId, "clientIcon")} alt={gameTitle} />}<Heading level="h1" color="white" className={gameTitleStyles}>{mapName} - {gameTitle}</Heading></>;
    $[22] = gameImageStyles;
    $[23] = gameTitle;
    $[24] = gameTitleStyles;
    $[25] = mapName;
    $[26] = steamId;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== gameInfoStyles || $[29] !== t16) {
    t17 = <div className={gameInfoStyles}>{t16}</div>;
    $[28] = gameInfoStyles;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      WebkitAppRegion: "no-drag"
    };
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  let t20;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <MapDetailsButton />;
    t20 = <MapShareButton />;
    $[32] = t19;
    $[33] = t20;
  } else {
    t19 = $[32];
    t20 = $[33];
  }
  let t21;
  if ($[34] !== isDesktop) {
    t21 = !isDesktop && <LanguageSelector />;
    $[34] = isDesktop;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== isDesktop) {
    t22 = !isDesktop && <MapTopBarUserAvatar />;
    $[36] = isDesktop;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t21 || $[39] !== t22) {
    t23 = <Suspense fallback={null}><FlexRow justify="center" align="center" gap="4" style={t18 as React.CSSProperties & {
        WebkitAppRegion?: string;
      }}>{t19}{t20}{t21}{t22}</FlexRow></Suspense>;
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== downloadButtonText) {
    t24 = <Button color="white">{downloadButtonText}</Button>;
    $[41] = downloadButtonText;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== downloadPath || $[44] !== t24) {
    t25 = <Link href={downloadPath}>{t24}</Link>;
    $[43] = downloadPath;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== downloadButtonText || $[47] !== downloadPath) {
    t26 = <MapTopBarActions downloadButtonText={downloadButtonText} downloadPath={downloadPath} />;
    $[46] = downloadButtonText;
    $[47] = downloadPath;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== t25 || $[50] !== t26) {
    t27 = <Suspense fallback={t25}>{t26}</Suspense>;
    $[49] = t25;
    $[50] = t26;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== actionsContainerStyles || $[53] !== t23 || $[54] !== t27) {
    t28 = <FlexRow align="center" gap="8" className={actionsContainerStyles}>{t23}{t27}</FlexRow>;
    $[52] = actionsContainerStyles;
    $[53] = t23;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t15 || $[57] !== t17 || $[58] !== t28 || $[59] !== topBarStyles) {
    t29 = <header className={topBarStyles} style={t9 as React.CSSProperties & {
      WebkitAppRegion?: string;
    }}>{t15}{t17}{t28}</header>;
    $[56] = t15;
    $[57] = t17;
    $[58] = t28;
    $[59] = topBarStyles;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  return t29;
};